import React from "react";
import { Check, CreditCard, ShieldCheck, CalendarClock } from "lucide-react";
import { Container, Button, SectionHeading } from "@/components/primitives";
import { Reveal, Stagger, StaggerItem } from "@/components/Reveal";
import { PageHero, CTASection } from "@/components/sections";
import { useSeo } from "@/lib/seo";
import { insurance } from "@/data/site";

const icons = { CareCredit: CreditCard, "Meridian Membership": ShieldCheck, "Monthly Payment Plans": CalendarClock };

export default function Insurance() {
  useSeo({
    title: "Insurance & Financing | Meridian Dental Studio, Hell's Kitchen NYC",
    description: "We accept most major PPO dental insurance plans and offer CareCredit, membership plans and monthly financing. Affordable, transparent dental care in Hell's Kitchen, Manhattan.",
    path: "/insurance-financing",
  });

  return (
    <>
      <PageHero
        eyebrow="Insurance & Financing"
        title="Top-tier care that fits your budget."
        intro="We believe great dentistry should be accessible. We're in-network with most major PPO plans, file your claims for you, and offer flexible financing so cost never stands between you and a healthy smile."
      />

      <section className="pb-20">
        <Container>
          <SectionHeading eyebrow="In-network PPO plans" title="Insurance we work with." intro="Don't see your plan? We still may be able to help — call or text us to verify your benefits before your visit." />
          <Stagger className="mt-12 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
            {insurance.ppo.map((p) => (
              <StaggerItem key={p}>
                <div className="flex items-center gap-3 rounded-xl border border-[var(--border)] bg-[var(--surface)] px-5 py-5" data-testid={`insurance-${p.replace(/\s/g, "-")}`}>
                  <Check size={16} className="shrink-0 text-[var(--primary)]" />
                  <span className="text-sm font-medium text-[var(--fg)]">{p}</span>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
        </Container>
      </section>

      <section className="bg-[var(--surface)] py-24 sm:py-32">
        <Container>
          <SectionHeading eyebrow="No insurance? No problem." title="Flexible financing options." />
          <div className="mt-14 grid gap-6 md:grid-cols-3">
            {insurance.financing.map((f, i) => {
              const Icon = icons[f.name] || CreditCard;
              return (
                <Reveal key={f.name} delay={i * 0.08}>
                  <div className="flex h-full flex-col rounded-2xl border border-[var(--border)] bg-[var(--bg)] p-8">
                    <Icon size={26} className="text-[var(--primary)]" />
                    <h3 className="mt-5 font-heading text-2xl font-normal text-[var(--fg)]">{f.name}</h3>
                    <p className="mt-3 flex-1 text-sm leading-relaxed text-[var(--muted-fg)]">{f.desc}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
          <Reveal delay={0.1}>
            <div className="mt-10 rounded-2xl border border-[var(--border)] bg-[var(--bg)] p-8 text-center">
              <p className="mx-auto max-w-2xl text-sm leading-relaxed text-[var(--muted-fg)]">
                Every treatment plan comes with a clear, itemized estimate before we begin — no surprises. Our patient coordinator will walk you through insurance, financing and payment options at your first visit.
              </p>
              <Button to="/contact" className="mt-6" data-testid="insurance-contact-btn">Ask about your options</Button>
            </div>
          </Reveal>
        </Container>
      </section>

      <CTASection eyebrow="Let's make it simple" title="Book now — we'll handle the paperwork." />
    </>
  );
}
